import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, FileUp, X, FileText, ImageIcon, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";

const MAX_MB = 10;
const MAX_PAGES = 10;
const OK_IMG = ["image/jpeg", "image/png", "image/webp"];

export default function UploadPage() {
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const imgRef = useRef(null);
  const pdfRef = useRef(null);
  const [items, setItems] = useState([]); // [{file, url, id}]
  const [pdfFile, setPdfFile] = useState(null);
  const { language } = useApp();

  const totalSize = items.reduce((s, it) => s + it.file.size, 0) + (pdfFile?.size || 0);

  const addImages = (fileList) => {
    if (pdfFile) { toast.error("PDF und Fotos können nicht gemischt werden."); return; }
    const files = Array.from(fileList || []);
    let next = [...items];
    for (const f of files) {
      if (!OK_IMG.includes(f.type)) { toast.error(`${f.name}: Format nicht unterstützt`); continue; }
      if (next.length >= MAX_PAGES) { toast.error(`Maximal ${MAX_PAGES} Seiten.`); break; }
      const size = next.reduce((s, it) => s + it.file.size, 0) + f.size;
      if (size > MAX_MB * 1024 * 1024) { toast.error("Gesamtgröße überschreitet 10 MB."); break; }
      next.push({ file: f, url: URL.createObjectURL(f), id: crypto.randomUUID() });
    }
    setItems(next);
  };

  const addPdf = (fileList) => {
    if (items.length) { toast.error("PDF und Fotos können nicht gemischt werden."); return; }
    const f = fileList?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Nur PDF."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error("Datei zu groß."); return; }
    setPdfFile(f);
  };

  const remove = (id) => setItems(items.filter(it => it.id !== id));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const cp = [...items]; [cp[idx], cp[j]] = [cp[j], cp[idx]]; setItems(cp);
  };
  const removePdf = () => setPdfFile(null);

  const analyze = () => {
    if (pdfFile) {
      navigate("/analyzing", { state: { files: [pdfFile], target_language: language } });
    } else if (items.length > 0) {
      navigate("/analyzing", { state: { files: items.map(it => it.file), target_language: language } });
    }
  };

  const totalPages = items.length || (pdfFile ? 1 : 0);
  const canAnalyze = totalPages > 0;

  return (
    <div className="px-5 pt-6 pb-8" data-testid="upload-page">
      <h1 className="text-2xl font-bold text-slate-900">Brief hochladen</h1>
      <p className="mt-1 text-slate-600 text-[15px]">
        Fotografiere alle Seiten gut lesbar. Mehrseitige Briefe: einfach mehrere Fotos hinzufügen.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <button
          onClick={() => cameraRef.current?.click()}
          data-testid="upload-camera-btn"
          className="flex flex-col items-center justify-center gap-2 min-h-[110px] rounded-3xl bg-blue-600 text-white font-semibold active:scale-[0.98] transition-transform shadow-md shadow-blue-600/20"
        >
          <Camera className="w-8 h-8" />
          <span className="text-sm">Foto aufnehmen</span>
        </button>
        <button
          onClick={() => imgRef.current?.click()}
          data-testid="upload-gallery-btn"
          className="flex flex-col items-center justify-center gap-2 min-h-[110px] rounded-3xl bg-blue-50 text-blue-700 font-semibold active:scale-[0.98] transition-transform border border-blue-100"
        >
          <Plus className="w-8 h-8" />
          <span className="text-sm">Bilder wählen</span>
        </button>
      </div>
      <button
        onClick={() => pdfRef.current?.click()}
        data-testid="upload-pdf-btn"
        className="mt-3 w-full flex items-center justify-center gap-2 min-h-[52px] rounded-2xl bg-white text-slate-700 font-medium border border-slate-200"
      >
        <FileUp className="w-5 h-5" /> PDF hochladen
      </button>

      <input ref={cameraRef} type="file" accept="image/*" capture="environment"
             className="hidden" onChange={e => addImages(e.target.files)} data-testid="camera-input" />
      <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
             className="hidden" onChange={e => addImages(e.target.files)} data-testid="images-input" />
      <input ref={pdfRef} type="file" accept="application/pdf"
             className="hidden" onChange={e => addPdf(e.target.files)} data-testid="pdf-input" />

      {items.length > 0 && (
        <div className="mt-6" data-testid="pages-list">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Seiten</div>
            <div className="text-xs text-slate-500">{items.length} / {MAX_PAGES} · {(totalSize/1024).toFixed(0)} KB</div>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={it.id} className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`page-item-${idx}`}>
                <div className="w-6 text-center font-semibold text-blue-700">{idx + 1}</div>
                <img src={it.url} alt="page" className="w-14 h-16 object-cover rounded-lg" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{it.file.name}</div>
                  <div className="text-xs text-slate-500">{(it.file.size / 1024).toFixed(0)} KB</div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`page-up-${idx}`}
                    className="p-1 rounded-lg disabled:opacity-30 hover:bg-slate-100"><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} data-testid={`page-down-${idx}`}
                    className="p-1 rounded-lg disabled:opacity-30 hover:bg-slate-100"><ArrowDown className="w-4 h-4" /></button>
                </div>
                <button onClick={() => remove(it.id)} data-testid={`page-remove-${idx}`}
                  className="p-2 rounded-full hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
              </div>
            ))}
          </div>
          <button onClick={() => imgRef.current?.click()} data-testid="add-more-btn"
            className="mt-3 w-full min-h-[48px] rounded-2xl bg-blue-50 text-blue-700 font-semibold flex items-center justify-center gap-2 border border-blue-100">
            <Plus className="w-4 h-4" /> Weitere Seite hinzufügen
          </button>
        </div>
      )}

      {pdfFile && (
        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-4" data-testid="pdf-preview">
          <div className="flex items-start gap-3">
            <div className="w-16 h-20 rounded-xl bg-blue-50 flex items-center justify-center">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">{pdfFile.name}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                <ImageIcon className="w-3.5 h-3.5" /><span>PDF</span><span>•</span><span>{(pdfFile.size/1024).toFixed(0)} KB</span>
              </div>
            </div>
            <button onClick={removePdf} data-testid="remove-pdf-btn" className="p-2 rounded-full hover:bg-slate-100">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={analyze}
        disabled={!canAnalyze}
        data-testid="analyze-btn"
        className="mt-8 w-full min-h-[60px] rounded-3xl bg-blue-600 text-white font-semibold disabled:bg-slate-200 disabled:text-slate-400 active:scale-[0.98] transition-transform shadow-md shadow-blue-600/20"
      >
        {totalPages > 1 ? `${totalPages} Seiten analysieren` : "Brief analysieren"}
      </button>

      <p className="mt-3 text-center text-xs text-slate-500">JPG, PNG oder PDF · max. 10 MB · bis zu {MAX_PAGES} Seiten</p>
    </div>
  );
}
